import React from 'react';
import RouterInterfaceTable from '../components/RouterInterfaceTable';
import RouterInfoPanel from '../components/RouterInfoPanel';
import RouterListPanel from '../components/RouterListPanel';
import RouterMonitoringFilterBar from '../components/RouterMonitoringFilterBar';
import RouterStatusCard from '../components/RouterStatusCard';
import RouterTrafficChart from '../components/RouterTrafficChart';
import RouterTrafficSummaryCards from '../components/RouterTrafficSummaryCards';
import { useRouterMonitoringData } from '../hooks/useRouterMonitoringData';
import { AppShell, PageContainer } from '../../ui-system';

const aggregateHistorySamples = (samples = [], selectedInterfaces = []) => {
  const items = Array.isArray(samples) ? samples : [];
  const selectedSet = new Set((Array.isArray(selectedInterfaces) ? selectedInterfaces : []).filter(Boolean));
  const filtered = selectedSet.size > 0
    ? items.filter((row) => selectedSet.has(String(row?.interface_name || row?.interfaceName || '').trim()))
    : items;
  const byTimestamp = new Map();
  filtered.forEach((row) => {
    const ts = String(row?.timestamp || '').trim();
    if (!ts) return;
    const prev = byTimestamp.get(ts) || { ...row, in_bps: 0, out_bps: 0 };
    prev.in_bps = Number(prev.in_bps || 0) + Number(row?.in_bps || 0);
    prev.out_bps = Number(prev.out_bps || 0) + Number(row?.out_bps || 0);
    byTimestamp.set(ts, prev);
  });
  return Array.from(byTimestamp.values())
    .sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
};

const RouterMonitoringPage = () => {
  const {
    filters,
    routersQuery,
    routers,
    selectedRouterId,
    setSelectedRouterId,
    selectedRouter,
    currentQuery,
    interfacesQuery,
    historyQuery,
  } = useRouterMonitoringData();

  const currentSummary = currentQuery.data?.summary || {
    total_in_bps: 0,
    total_out_bps: 0,
    monitored_interface_count: 0,
    active_ppp_sessions: selectedRouter?.activePppSessions || selectedRouter?.active_ppp_sessions || 0,
  };
  const interfaceRows = interfacesQuery.data?.interfaces || [];
  const historySamples = historyQuery.data?.samples || [];
  const isOffline = selectedRouter && !Boolean(selectedRouter.isOnline ?? selectedRouter.is_online);
  const interfaceOptions = [
    ...new Set(
      [
        selectedRouter?.primaryUplinkInterface,
        selectedRouter?.recommendedPrimaryUplinkInterface,
        ...(selectedRouter?.monitoredInterfaces || []),
        ...interfaceRows.map((item) => item.name).filter(Boolean),
      ].filter(Boolean),
    ),
  ];
  const selectedInterfaces = Array.isArray(filters.selectedInterfaces) ? filters.selectedInterfaces : [];
  const mergedHistorySamples = aggregateHistorySamples(historySamples, selectedInterfaces);

  return (
    <AppShell>
      <PageContainer>
      <div className="w-full space-y-4">
        <header className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Router Monitoring</div>
          <h1 className="mt-1 text-2xl font-semibold">Status Router dan Riwayat Trafik</h1>
          <p className="mt-1 text-sm text-slate-400">Pantau status perangkat, uplink utama, interface monitoring, trafik realtime, dan riwayat trafik.</p>
        </header>

        <RouterMonitoringFilterBar filters={filters} onRefresh={() => {
          routersQuery.refetch();
          currentQuery.refetch();
          interfacesQuery.refetch();
          historyQuery.refetch();
        }}
        />

        <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <RouterListPanel
            routers={routers}
            selectedRouterId={selectedRouterId}
            onSelect={setSelectedRouterId}
            isLoading={routersQuery.isLoading}
          />

          <section className="space-y-4">
            <RouterStatusCard router={selectedRouter} />
            <RouterInfoPanel router={selectedRouter} />

            {isOffline ? (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                Perangkat Tidak Terjangkau. Data trafik mungkin tidak update real-time.
              </div>
            ) : null}

            {currentQuery.isError ? (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                Gagal memuat trafik saat ini: {String(currentQuery.error?.message || 'unknown error')}
              </div>
            ) : null}
            {interfacesQuery.isError ? (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                Gagal memuat data interface: {String(interfacesQuery.error?.message || 'unknown error')}
              </div>
            ) : null}
            {historyQuery.isError ? (
              <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                Gagal memuat riwayat trafik: {String(historyQuery.error?.message || 'unknown error')}
              </div>
            ) : null}

            <RouterTrafficSummaryCards summary={currentSummary} />

            {selectedRouter ? (
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex min-w-[280px] flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-[0.1em] text-slate-400">Interface Grafik (Multi Pilih)</span>
                    <div className="max-h-32 overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 p-2">
                      {interfaceOptions.length === 0 ? (
                        <p className="text-xs text-slate-500">Belum ada interface tersedia.</p>
                      ) : interfaceOptions.map((item) => {
                        const checked = selectedInterfaces.includes(item);
                        return (
                          <label key={item} className="flex items-center gap-2 py-1 text-xs text-slate-200">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const isChecked = event.target.checked;
                                const prev = Array.isArray(filters.selectedInterfaces) ? filters.selectedInterfaces : [];
                                const next = isChecked
                                  ? Array.from(new Set([...prev, item]))
                                  : prev.filter((name) => name !== item);
                                filters.setSelectedInterfaces(next);
                              }}
                            />
                            <span>{item}</span>
                          </label>
                        );
                      })}
                    </div>
                  </label>
                  <p className="text-xs text-slate-400">
                    Grafik menampilkan agregasi trafik dari interface yang dipilih.
                  </p>
                </div>
              </div>
            ) : null}

            <RouterTrafficChart
              historySamples={mergedHistorySamples}
              title={`Riwayat Trafik ${selectedInterfaces.length > 0 ? selectedInterfaces.join(', ') : '(Semua Interface)'}`}
            />

            <RouterInterfaceTable
              rows={interfaceRows}
              isLoading={interfacesQuery.isLoading || currentQuery.isLoading}
            />
          </section>
        </div>
      </div>
      </PageContainer>
    </AppShell>
  );
};

export default RouterMonitoringPage;
